import React, { useMemo } from 'react';
import { Box, Typography, makeStyles, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import AppButton from 'components/CustomButtons/AppButton';
import AppColor from 'utilities/AppColor';
import { useTranslation } from 'react-i18next';
import { GOAL_STATUS, MEASUREMENT_METHOD } from 'config/AppConfig';
import AppInput from 'components/Input/AppInput';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import SelectField from 'components/Select/SelectField';

const useStyles = makeStyles(() => ({
  cardHeader: {
    padding: '16px 20px',
    borderBottom: '1px solid #E5E7EB',
    background: '#FAFAFA',
    borderRadius: '6px 6px 0 0'
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111827',
  },
  cardContent: {
    padding: '20px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px'
  },
  radioLabel: {
    margin: 0,
    padding: '10px 15px',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F9FAFB'
    }
  },
  radioLabelActive: {
    borderColor: '#0D9488',
    backgroundColor: '#F0FDFA',
    '&:hover': {
      backgroundColor: '#F0FDFA'
    }
  },
  radioLabelDisabled: {
    opacity: 0.5,
    pointerEvents: 'none',
    backgroundColor: '#F3F4F6'
  },
  labelText: {
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
  },
  customRadio: {
    color: '#9CA3AF',
    '&$checked': {
      color: '#0D9488',
    },
  },
  checked: {},
  inputContainer: {
    marginTop: 15,
  }
}));

const UpdateGoalStatusModal = ({ open, onClose, onConfirm, goal, isLoading, questions = [] }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isAchievedAllowed = goal && (
    goal.measurementMethod === MEASUREMENT_METHOD.OBSERVATION ||
    (goal.progressPercentage >= 100)
  );
  
  const statusOptions = useMemo(() => {
    if (!goal) return [];
    
    const options = [];
    const currentStatus = goal.status;

    if (currentStatus === GOAL_STATUS.DRAFT) {
      options.push({ value: GOAL_STATUS.DISCONTINUED, label: t('goalStatus.DISCONTINUED', 'Discontinued') });
    } else if (currentStatus === GOAL_STATUS.ACTIVE) {
      options.push({ value: GOAL_STATUS.DISCONTINUED, label: t('goalStatus.DISCONTINUED', 'Discontinued') });
      if (isAchievedAllowed) {
        options.push({ value: GOAL_STATUS.ACHIEVED, label: t('goalStatus.ACHIEVED', 'Achieved') });
      }
    } else if (currentStatus === GOAL_STATUS.DISCONTINUED) {
      options.push({ value: GOAL_STATUS.ACTIVE, label: t('goalStatus.ACTIVE', 'Active') });
    }

    return options;
  }, [goal, isAchievedAllowed, t]);

  const currentQuestionOptions = useMemo(() => {
    return questions
      .filter(q => goal?.measurementMethod !== MEASUREMENT_METHOD.FREQUENCY_COUNT || q.questionType === "YES_NO")
      .map(q => ({
        value: q.id,
        label: q.question
      }));
  }, [questions, goal]);

  if (!open || !goal) return null;

  const initialValues = {
    status: '',
    targetDate: goal.targetDate ? moment(goal.targetDate, "YYYY/MM/DD").format("YYYY/MM/DD") : '',
    reasonDiscontinued: '',
    questionId: goal.questionId ? goal.questionId : '',
  };


  const validationSchema = Yup.object().shape({
    status: Yup.string().required(t('validation.required')),
    targetDate: Yup.string().when('status', {
      is: GOAL_STATUS.ACTIVE,
      then: (schema) => schema.required(t('validation.required')),
      otherwise: (schema) => schema.notRequired()
    }),
    questionId: Yup.string().when('status', {
      is: GOAL_STATUS.ACTIVE,
      then: (schema) => schema.required(t('validation.required')),
      otherwise: (schema) => schema.notRequired()
    }),
    reasonDiscontinued: Yup.string().when('status', {
      is: GOAL_STATUS.DISCONTINUED,
      then: (schema) => schema.required(t('validation.required')).trim(),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const handleSubmit = (values) => {
    let payload = { status: values.status };
    if (values.status === GOAL_STATUS.ACTIVE) {
      payload.targetDate = moment(values.targetDate).format("YYYY/MM/DD");
      payload.question_ids = values.questionId ? [Number(values.questionId)] : [];
    } else if (values.status === GOAL_STATUS.DISCONTINUED) {
      payload.reasonDiscontinued = values.reasonDiscontinued;
    }

    if (onConfirm) {
      onConfirm(payload);
    }
  };

  return (
    <Box>
      <Box className={classes.cardHeader}>
        <Typography className={classes.cardTitle}>
          {goal.name}
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Box className={classes.cardContent}>
              {statusOptions.length === 0 ? (
                <Typography color="error">{t("goal.no_status_available", "No status transition available")}</Typography>
              ) : (
                <RadioGroup
                  name="status"
                  value={values.status}
                  onChange={(e) => {
                    setFieldValue('status', e.target.value);
                    if (e.target.value !== GOAL_STATUS.ACTIVE && e.target.value !== GOAL_STATUS.DISCONTINUED) {
                      setFieldValue('targetDate', '');
                      setFieldValue('reasonDiscontinued', '');
                    }
                  }}
                  className={classes.radioGroup}
                >
                  {statusOptions.map((option) => {
                    const isActive = values.status === option.value;
                    let labelClass = classes.radioLabel;
                    if (isActive) labelClass += ` ${classes.radioLabelActive}`;

                    return (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={
                          <Radio 
                            classes={{ root: classes.customRadio, checked: classes.checked }} 
                            disabled={isLoading} 
                          />
                        }
                        label={<span className={classes.labelText}>{option.label}</span>}
                        className={labelClass}
                      />
                    );
                  })}
                </RadioGroup>
              )}

              {values.status === GOAL_STATUS.ACTIVE && (
                <>
                  <Box className={classes.inputContainer}>
                    <Field
                      component={CustomDatePicker}
                      name="targetDate"
                      label="goal.targetDate"
                      formatDateJob={true}
                      custom={{ disablePast: true }}
                      required
                    />
                  </Box>
                  {goal.status === GOAL_STATUS.DISCONTINUED && (
                    <Box className={classes.inputContainer}>
                      <Field
                        component={SelectField}
                        name="questionId"
                        label="goal.linkedQuestionPrompt"
                        options={currentQuestionOptions}
                        required
                      />
                    </Box>
                  )}
                </>
              )}

              {values.status === GOAL_STATUS.DISCONTINUED && (
                <Box className={classes.inputContainer}>
                  <Field
                    component={AppInput}
                    name="reasonDiscontinued"
                    label="goal.reasonDiscontinued"
                    placeholder={t("goal.reasonDiscontinuedPlaceholder", "Enter reason")}
                    multiline
                    rows={3}
                    type="textarea"
                    required
                  />
                </Box>
              )}
            </Box>

            <Box className={classes.modalActions}>
              <AppButton
                label={t("button.cancel")}
                mode="outlined"
                onClick={onClose}
                disabled={isLoading}
              />
              <AppButton
                label={t("button.save_changes")}
                buttonColor={AppColor.mainColor}
                type="submit"
                disabled={isLoading || !values.status}
                isLoading={isLoading}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateGoalStatusModal;
